import React, {memo, useEffect} from "react";
import {motion} from "framer-motion";
import {FaBolt, FaCamera, FaGraduationCap, FaPuzzlePiece,} from "react-icons/fa";
import Header from "./Header";

interface FeatureItemProps {
    icon: React.ReactNode;
    text: string;
    bgColor: string; // New property for background color
    iconColor: string; // New property for icon color
}

// More concise features list
const FEATURES: FeatureItemProps[] = [
    {
        icon: <FaCamera className="w-5 h-5"/>,
        text: "Snap photo of any equation",
        bgColor: "bg-purple-500 dark:bg-purple-600",
        iconColor: "text-white",
    },
    {
        icon: <FaBolt className="w-5 h-5"/>,
        text: "Get instant solutions",
        bgColor: "bg-green-500 dark:bg-green-600",
        iconColor: "text-white",
    },
    {
        icon: <FaGraduationCap className="w-5 h-5"/>,
        text: "Learn with step-by-step help",
        bgColor: "bg-orange-500 dark:bg-orange-600",
        iconColor: "text-white",
    },
    {
        icon: <FaPuzzlePiece className="w-5 h-5"/>,
        text: "All math subjects covered",
        bgColor: "bg-teal-500 dark:bg-teal-600",
        iconColor: "text-white",
    },
];

// Animation variants
const containerVariants = {
    hidden: {opacity: 0},
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
        },
    },
};

const itemVariants = {
    hidden: {opacity: 0, y: 20},
    visible: {opacity: 1, y: 0, transition: {duration: 0.5}},
};

const Hero = () => {
    useEffect(() => {
        if (!document.querySelector('meta[name="theme-color"]')) {
            const metaThemeColor = document.createElement("meta");
            metaThemeColor.name = "theme-color";
            metaThemeColor.content = "#0284c7"; // blue-600
            document.head.appendChild(metaThemeColor);
        }
    }, []);

    return (
        <div className="min-h-screen flex flex-col relative overflow-hidden">
            {/* Consistent gradient background */}
            <div
                className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 dark:from-blue-900 dark:via-blue-800 dark:to-indigo-900"/>

            {/* Math grid pattern background */}
            <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>

            {/* Math symbols background - more consistent placement */}
            <div className="absolute inset-0 overflow-hidden opacity-15">
                {[
                    {symbol: "∫", position: "top-1/4 left-1/4", size: "text-8xl"},
                    {symbol: "∑", position: "top-3/4 left-1/6", size: "text-7xl"},
                    {symbol: "π", position: "top-1/3 right-1/5", size: "text-8xl"},
                    {symbol: "√", position: "top-2/3 right-1/4", size: "text-7xl"},
                    {symbol: "±", position: "bottom-1/4 left-2/5", size: "text-6xl"},
                    {symbol: "×", position: "top-1/2 left-3/4", size: "text-5xl"},
                    {symbol: "÷", position: "bottom-1/3 right-1/3", size: "text-6xl"},
                    {symbol: "θ", position: "top-1/5 right-2/3", size: "text-5xl"},
                    {symbol: "λ", position: "bottom-2/5 right-2/5", size: "text-7xl"},
                    {symbol: "Δ", position: "top-3/5 left-1/3", size: "text-6xl"},
                ].map((item, index) => (
                    <div
                        key={index}
                        className={`absolute ${item.size} font-bold text-white ${item.position}`}
                    >
                        {item.symbol}
                    </div>
                ))}
            </div>

            {/* Floating elements - more consistent coloring */}
            <FloatingElements/>

            {/* Header with logo and download button */}
            <Header/>

            {/* Main content */}
            <main className="flex-grow px-6 pt-8 pb-16 md:pt-12 md:pb-24 relative z-10">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="max-w-7xl mx-auto"
                >
                    <div className="flex flex-col md:flex-row items-center justify-between gap-12 lg:gap-20">
                        {/* Left column - content */}
                        <ContentSection features={FEATURES}/>

                        {/* Right column - device mockup */}
                        <DeviceMockup/>
                    </div>
                </motion.div>
            </main>

            {/* Footer */}
            <Footer/>
        </div>
    );
};

// More consistent floating elements with matching color scheme
const FloatingElements = memo(() => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
            {
                className: "w-64 h-64 bg-blue-400/20 dark:bg-blue-600/20",
                position: {top: "15%", left: "5%"},
                animation: {y: [0, 15, 0], x: [0, 10, 0]},
                duration: 8,
            },
            {
                className: "w-40 h-40 bg-indigo-400/20 dark:bg-indigo-600/20",
                position: {top: "60%", right: "10%"},
                animation: {y: [0, -20, 0], x: [0, -5, 0]},
                duration: 10,
            },
            {
                className: "w-48 h-48 bg-blue-500/20 dark:bg-blue-700/20",
                position: {bottom: "20%", left: "15%"},
                animation: {y: [0, 10, 0], x: [0, 5, 0]},
                duration: 9,
            },
            {
                className: "w-32 h-32 bg-indigo-500/20 dark:bg-indigo-700/20",
                position: {top: "40%", right: "25%"},
                animation: {y: [0, 15, 0], x: [0, -10, 0]},
                duration: 7,
            },
            {
                className: "w-56 h-56 bg-blue-300/20 dark:bg-blue-500/20",
                position: {bottom: "10%", right: "30%"},
                animation: {y: [0, -10, 0], x: [0, 8, 0]},
                duration: 11,
            },
        ].map((bubble, index) => (
            <motion.div
                key={index}
                className={`absolute rounded-full ${bubble.className}`}
                style={bubble.position}
                animate={bubble.animation}
                transition={{
                    repeat: Infinity,
                    duration: bubble.duration,
                    ease: "easeInOut",
                }}
            />
        ))}
    </div>
));

// Feature item component - with consistent colors
const FeatureItemComponent = memo(
    ({
         icon,
         text,
         bgColor,
         iconColor,
         index,
     }: {
        icon: React.ReactNode;
        text: string;
        bgColor: string;
        iconColor: string;
        index: number;
    }) => {
        return (
            <motion.div
                variants={itemVariants}
                className="flex items-center gap-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow border border-blue-100 dark:border-blue-900"
                whileHover={{scale: 1.02, transition: {duration: 0.2}}}
            >
                <div className={`${bgColor} p-2 rounded-lg shrink-0 ${iconColor}`}>
                    {icon}
                </div>
                <span className="text-gray-800 dark:text-gray-200 font-medium">
          {text}
        </span>
            </motion.div>
        );
    },
);

// Star rating component
const StarRating = memo(() => (
    <div className="flex">
        {Array(5)
            .fill(0)
            .map((_, i) => (
                <svg
                    key={i}
                    className="text-yellow-400 w-6 h-6"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
            ))}
    </div>
));

// Main content section
const ContentSection = memo(
    ({features}: { features: FeatureItemProps[] }) => (
        <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.8}}
            className="max-w-xl w-full"
        >
            <motion.h1
                variants={itemVariants}
                className="font-bold text-4xl md:text-5xl lg:text-6xl mb-4 text-white leading-tight drop-shadow-md"
            >
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
          Solve Any Math
        </span>
                <br/>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
          Problem Instantly
        </span>
            </motion.h1>

            <motion.p
                variants={itemVariants}
                className="text-lg text-white mb-6 leading-relaxed drop-shadow"
            >
                Take a photo & get step-by-step solutions in seconds!
            </motion.p>

            <motion.div
                variants={containerVariants}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8"
            >
                {features.map((feature, index) => (
                    <FeatureItemComponent
                        key={index}
                        icon={feature.icon}
                        text={feature.text}
                        bgColor={feature.bgColor}
                        iconColor={feature.iconColor}
                        index={index}
                    />
                ))}
            </motion.div>

            <motion.div variants={itemVariants} className="flex justify-start mb-8">
                <motion.a
                    href="https://apps.apple.com/app/id6742758457"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group"
                    whileHover={{scale: 1.05}}
                    initial={{scale: 1}}
                >
                    <img
                        src="/appstore.svg"
                        alt="Download on the App Store"
                        className="h-[60px] md:h-[70px] shadow-md rounded-lg transition-all group-hover:shadow-lg"
                        draggable="false"
                    />
                    <div
                        className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-700 text-white text-xs py-1 px-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        Free Download
                    </div>
                </motion.a>
            </motion.div>

            <motion.div
                variants={itemVariants}
                className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-5 rounded-2xl shadow-lg border border-blue-100 dark:border-blue-900"
            >
                <div className="flex items-center gap-4">
                    <StarRating/>
                    <div className="flex flex-col">
            <span className="text-gray-800 dark:text-white font-semibold">
              4.9 out of 5
            </span>
                        <span className="text-gray-600 dark:text-gray-400 text-sm">
              50,000+ students ♥ our app
            </span>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    ),
);

// Device mockup section - with consistent animation
const DeviceMockup = memo(() => (
    <motion.div
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        transition={{duration: 0.8}}
        className="w-full max-w-sm md:max-w-md relative"
    >
        <div className="relative w-full overflow-hidden">
            <motion.div
                className="relative"
                animate={{scale: [1, 1.02, 1]}}
                transition={{repeat: Infinity, duration: 4, ease: "easeInOut"}}
            >
                <img
                    src="/promo.png"
                    alt="Math Solver App Interface"
                    className="w-full h-auto object-contain relative z-0 drop-shadow-2xl"
                    draggable="false"
                />

                {/* Add floating mathematical symbols around the device - consistent with theme */}
                <motion.div
                    className="absolute -top-10 -right-10 text-4xl text-blue-200 font-bold z-10"
                    animate={{y: [0, -8, 0], rotate: [0, 5, 0]}}
                    transition={{repeat: Infinity, duration: 4, ease: "easeInOut"}}
                >
                    ∫
                </motion.div>
                <motion.div
                    className="absolute -bottom-10 -left-10 text-4xl text-blue-200 font-bold z-10"
                    animate={{y: [0, 8, 0], rotate: [0, -5, 0]}}
                    transition={{repeat: Infinity, duration: 4, ease: "easeInOut"}}
                >
                    π
                </motion.div>
                <motion.div
                    className="absolute top-1/2 -right-8 text-4xl text-blue-200 font-bold z-10"
                    animate={{x: [0, 5, 0], rotate: [0, 8, 0]}}
                    transition={{repeat: Infinity, duration: 5, ease: "easeInOut"}}
                >
                    √
                </motion.div>
            </motion.div>
        </div>
    </motion.div>
));

// Footer component
const Footer = memo(() => (
    <footer
        className="py-4 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-500">
                <a
                    href="/privacy-policy"
                    className="hover:text-blue-600 transition-colors"
                >
                    Privacy
                </a>
                <span className="hidden md:inline">•</span>
                <a
                    href="/terms-of-service"
                    className="hover:text-blue-600 transition-colors"
                >
                    Terms
                </a>
                <span className="hidden md:inline">•</span>
                <a href="/help" className="hover:text-blue-600 transition-colors">
                    Help
                </a>
                <span className="hidden md:inline">•</span>
                <a href="/contact" className="hover:text-blue-600 transition-colors">
                    Contact
                </a>
            </div>
        </div>
    </footer>
));

export default memo(Hero);
